import * as React from 'react';
import {Query, Mutation} from 'react-apollo';

import type {
    CreateStepTemplateInputFieldInput,
    UpdateStepTemplateInputFieldInput,
    DeleteStepTemplateInputFieldInput,
    CreateStepTemplate as CreateStepTemplateMutation,
    CreateStepTemplateVariables as CreateStepTemplateMutationVariables,
    UpdateStepTemplate as UpdateStepTemplateMutation,
    UpdateStepTemplateVariables as UpdateStepTemplateMutationVariables,
    DeleteStepTemplate as DeleteStepTemplateMutation,
    DeleteStepTemplateVariables as DeleteStepTemplateMutationVariables,
    GetStepTemplate as GetStepTemplateQuery,
    GetStepTemplateVariables as GetStepTemplateQueryVariables,
    GetStepTemplate_stepTemplate,
    StepTemplatesForStageQuery as StepTemplatesForStageQueryQuery,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../apollo-client';

import {refetchSalesAutomationOpenLeadQueries} from '../app/sales-automation-open-leads';
import CREATE_STEP_TEMPLATE from '../graphql/mutations/create-step-template.graphql';
import DELETE_STEP_TEMPLATE from '../graphql/mutations/delete-step-template.graphql';
import UPDATE_STEP_TEMPLATE from '../graphql/mutations/update-step-template.graphql';
import GET_STEP_TEMPLATE from '../graphql/queries/get-step-template.graphql';
import GET_STEP_TEMPLATES_FOR_STAGE from '../graphql/queries/get-step-templates-for-stage.graphql';

import {TodoTemplateForm} from './todo-template-form';
import type {TodoTemplateInput} from './todo-template-form';

type GraphQLProps = {
    stepTemplate: GetStepTemplate_stepTemplate | null | undefined;
    isFetchingLatestVersion: boolean;
    createStepTemplate: (arg1: CreateStepTemplateInputFieldInput) => Promise<any>;
    updateStepTemplate: (arg1: UpdateStepTemplateInputFieldInput) => Promise<any>;
    deleteStepTemplate: (arg1: DeleteStepTemplateInputFieldInput) => Promise<any>;
};

type Props = OwnProps & GraphQLProps;

/*
 * Wrapper component around our step templates form that hooks up to GraphQL
 * and passes down the relevant (cached) step template, and step template mutations:
 *
 * - CreateStepTemplate
 * - UpdateStepTemplate
 * - DeleteStepTemplate
 */
export class FormStepTemplatesComponent extends React.PureComponent<Props> {
    render() {
        return (
            <TodoTemplateForm
                formSubmitBtnRef={this.props.formSubmitBtnRef}
                todoTemplate={this.props.stepTemplate}
                isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                onSubmit={this.handleSubmit}
                onDelete={this.handleDelete}
            />
        );
    }

    handleDelete = (entity: {id: string}) => {
        return this.props.deleteStepTemplate(entity);
    };

    handleSubmit = (formData: TodoTemplateInput) => {
        const saveStepTemplatePromise = this.props.stepTemplate
            ? this.props.updateStepTemplate({
                  id: this.props.stepTemplate.id,
                  stageId: this.props.stageId,
                  name: formData.name,
                  description: formData.description,
                  dueDate: formData.dueDate
                      ? {
                            dueFrom: formData.dueDate.dueFrom,
                            dueUnit: formData.dueDate.dueUnit,
                            dueTimespan: formData.dueDate.dueTimespan,
                        }
                      : null,
                  assigneeId: formData.assigneeId,
              })
            : this.props.createStepTemplate({
                  stageId: this.props.stageId,
                  name: formData.name,
                  description: formData.description,
                  dueDate: formData.dueDate
                      ? {
                            dueFrom: formData.dueDate.dueFrom,
                            dueUnit: formData.dueDate.dueUnit,
                            dueTimespan: formData.dueDate.dueTimespan,
                        }
                      : null,
                  assigneeId: formData.assigneeId,
              });

        return saveStepTemplatePromise;
    };
}

type OwnProps = {
    entityId: string | null | undefined;
    stageId: string;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
};

class StepTemplateQuery extends Query<GetStepTemplateQuery, GetStepTemplateQueryVariables> {}
class CreationMutation extends Mutation<
    CreateStepTemplateMutation,
    CreateStepTemplateMutationVariables
> {}
class UpdatingMutation extends Mutation<
    UpdateStepTemplateMutation,
    UpdateStepTemplateMutationVariables
> {}
class DeletionMutation extends Mutation<
    DeleteStepTemplateMutation,
    DeleteStepTemplateMutationVariables
> {}

export const FormStepTemplates = (props: OwnProps) => (
    <StepTemplateQuery
        query={GET_STEP_TEMPLATE}
        skip={!props.entityId}
        variables={{
            id: props.entityId || '', // Safe because of skip prop
        }}
        // @ts-expect-error
        fetchPolicy='cache-and-network'
    >
        {({data, loading}) => (
            <CreationMutation
                mutation={CREATE_STEP_TEMPLATE}
                // @ts-expect-error
                refetchQueries={refetchSalesAutomationOpenLeadQueries({stageId: props.stageId})}
                update={(proxy, mutationResult) => {
                    if (
                        !mutationResult ||
                        !mutationResult.data ||
                        !mutationResult.data.createStepTemplate
                    ) {
                        return;
                    }

                    const createStepTemplate = mutationResult.data.createStepTemplate;

                    // Read the data from our cache for this query.
                    const cacheResult: StepTemplatesForStageQueryQuery | null | undefined =
                        proxy.readQuery({
                            query: GET_STEP_TEMPLATES_FOR_STAGE,
                            variables: {stageId: props.stageId},
                        });

                    if (!cacheResult) return;

                    // Add our todo from the mutation to the end.
                    cacheResult.editableStepTemplates = cacheResult.editableStepTemplates.concat([
                        createStepTemplate.stepTemplate,
                    ]);

                    // Write our data back to the cache.
                    proxy.writeQuery({
                        query: GET_STEP_TEMPLATES_FOR_STAGE,
                        variables: {stageId: props.stageId},
                        data: cacheResult,
                    });
                }}
            >
                {(createStep) => (
                    <UpdatingMutation
                        mutation={UPDATE_STEP_TEMPLATE}
                        // @ts-expect-error
                        refetchQueries={refetchSalesAutomationOpenLeadQueries({
                            stageId: props.stageId,
                        })}
                        update={(proxy, mutationResult) => {
                            if (
                                !mutationResult ||
                                !mutationResult.data ||
                                !mutationResult.data.updateStepTemplate
                            ) {
                                return;
                            }
                            const updateStepTemplate = mutationResult.data.updateStepTemplate;
                            const cacheResult: StepTemplatesForStageQueryQuery | null | undefined =
                                proxy.readQuery({
                                    query: GET_STEP_TEMPLATES_FOR_STAGE,
                                    variables: {stageId: props.stageId},
                                });

                            if (!cacheResult) return;

                            // Add our todo from the mutation to the end.
                            const stepTemplatesWithoutUpdatedStepTemplate =
                                cacheResult.editableStepTemplates.filter(
                                    (stepTemplateToCheck) =>
                                        stepTemplateToCheck.id !== props.entityId
                                );
                            const newStepTemplate = {
                                ...updateStepTemplate.stepTemplate,
                            } as const;

                            const newStepTemplates = stepTemplatesWithoutUpdatedStepTemplate.concat(
                                [newStepTemplate]
                            );

                            cacheResult.editableStepTemplates = newStepTemplates;

                            // Write our data back to the cache.
                            proxy.writeQuery({
                                query: GET_STEP_TEMPLATES_FOR_STAGE,
                                variables: {stageId: props.stageId},
                                data: cacheResult,
                            });
                        }}
                    >
                        {(updateStep) => (
                            <DeletionMutation
                                mutation={DELETE_STEP_TEMPLATE}
                                // @ts-expect-error
                                refetchQueries={refetchSalesAutomationOpenLeadQueries({
                                    stageId: props.stageId,
                                })}
                                update={(proxy) => {
                                    const cacheResult:
                                        | StepTemplatesForStageQueryQuery
                                        | null
                                        | undefined = nutApolloClient.readQuery({
                                        query: GET_STEP_TEMPLATES_FOR_STAGE,
                                        variables: {stageId: props.stageId},
                                    });
                                    if (!cacheResult) return;

                                    // Add our todo from the mutation to the end.
                                    const newStepTemplates =
                                        cacheResult.editableStepTemplates.filter(
                                            (stepTemplateToCheck) =>
                                                stepTemplateToCheck.id !== props.entityId
                                        );
                                    cacheResult.editableStepTemplates = newStepTemplates;

                                    // Write our data back to the cache.
                                    proxy.writeQuery({
                                        query: GET_STEP_TEMPLATES_FOR_STAGE,
                                        variables: {stageId: props.stageId},
                                        data: cacheResult,
                                    });
                                }}
                            >
                                {(deleteStep) => {
                                    const stepTemplate = data ? data.stepTemplate : null;

                                    return (
                                        <FormStepTemplatesComponent
                                            {...props}
                                            stepTemplate={stepTemplate}
                                            isFetchingLatestVersion={loading}
                                            createStepTemplate={(createStepTemplatePayload) =>
                                                createStep({
                                                    variables: {
                                                        stepTemplate: createStepTemplatePayload,
                                                    },
                                                })
                                            }
                                            updateStepTemplate={(updateStepTemplatePayload) => {
                                                return updateStep({
                                                    variables: {
                                                        stepTemplate: updateStepTemplatePayload,
                                                    },
                                                });
                                            }}
                                            deleteStepTemplate={(deleteStepTemplatePayload) => {
                                                return deleteStep({
                                                    variables: {
                                                        stepTemplate: deleteStepTemplatePayload,
                                                    },
                                                });
                                            }}
                                        />
                                    );
                                }}
                            </DeletionMutation>
                        )}
                    </UpdatingMutation>
                )}
            </CreationMutation>
        )}
    </StepTemplateQuery>
);

// -----------------------------------------------------------------------------
//   StepTemplate query
// -----------------------------------------------------------------------------

// export const FormStepTemplates = compose(
//     withApollo,
//     withStepTemplate,
//     withCreationMutation,
//     withUpdateMutation,
//     withDeleteMutation
// )(FormStepTemplatesComponent);
